import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, Play, PackageCheck, ShieldAlert, AlarmClock, Search as SearchIcon, X, MessageSquare, FileDown } from "lucide-react";
import { formatBill } from "@/lib/format";
import UrgentReasonDialog from "@/components/UrgentReasonDialog";
import WhatsAppMessageModal, { buildCollectionReadyMessage } from "@/components/WhatsAppMessageModal";
import Pagination, { usePagination } from "@/components/Pagination";
import ImportButton from "@/components/ImportButton";

const RANGES = ["Today", "Yesterday", "Last 7 Days", "Last 30 Days", "Custom"];

// Reusable "assigned/login" listing page
export function AssignedListPage({
  endpoint, testid, title, subtitle, emptyLabel, showActionButton, statusFilter,
}) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isPrivileged = user?.role === "admin" || user?.role === "branch";
  const canChangeOperator = isAdmin || !!user?.permissions?.["Change Assigned Operator"]?.view;
  const canChangeStatus   = isAdmin || !!user?.permissions?.["Change Status"]?.view;
  // Ability to REVERT a status backward (Delivered → Completed etc). Reserved to admin/manager only.
  // Branch, staff and field-executive can only move status forward.
  const canOverrideStatus = user?.role === "admin" || user?.role === "manager";
  const canFlagUrgent     = isAdmin || !!user?.permissions?.["Urgent Work"]?.edit;
  const [urgentDlg, setUrgentDlg] = useState({ open: false, row: null });
  const [waRow, setWaRow] = useState(null);
  const storageKey = `crm:assigned:${user?.id || "anon"}:${endpoint}`;
  const savedRaw = typeof window !== "undefined" ? localStorage.getItem(storageKey) : null;
  const saved = savedRaw ? (() => { try { return JSON.parse(savedRaw); } catch { return {}; } })() : {};
  const today = new Date().toISOString().slice(0, 10);
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [operator, setOperator] = useState(saved.operator || "all");
  const [rng, setRng] = useState(saved.rng || "Today");
  const [dateFrom, setDateFrom] = useState(saved.dateFrom || today);
  const [dateTo, setDateTo] = useState(saved.dateTo || today);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [appIdEdit, setAppIdEdit] = useState({});
  const [remarkEdit, setRemarkEdit] = useState({});
  const [collectorEdit, setCollectorEdit] = useState({});
  const [lastSync, setLastSync] = useState(null);
  const paginationKey = `${rng}|${dateFrom}|${dateTo}|${operator}|${q}`;
  const { page, setPage, pageRows, totalPages, total, pageSize, setPageSize } = usePagination(rows, 50, paginationKey);

  // Persist filters
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({ operator, rng, dateFrom, dateTo }));
    } catch {}
  }, [storageKey, operator, rng, dateFrom, dateTo]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint, {
        params: { rng, date_from: dateFrom, date_to: dateTo, operator, q: q || undefined },
      });
      setRows(data);
      setLastSync(new Date());
    } finally { setLoading(false); }
  };
  useEffect(() => {
    if (isPrivileged) api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    /* eslint-disable-next-line */
  }, [isPrivileged]);
  useEffect(() => {
    load();
    /* eslint-disable-next-line */
  }, [rng, operator, dateFrom, dateTo]);

  // Debounced re-load when the search term changes
  useEffect(() => {
    const t = setTimeout(() => load(), 350);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [q]);

  // Auto-refresh every 30s
  useEffect(() => {
    const iv = setInterval(() => load(), 30000);
    return () => clearInterval(iv);
    /* eslint-disable-next-line */
  }, [rng, operator, dateFrom, dateTo, q]);

  const takeAction = async (r) => {
    const appId = (appIdEdit[r.id] ?? r.application_id ?? "").trim();
    if (!appId) {
      toast.error("Enter Application ID before starting work");
      return;
    }
    const remark = (remarkEdit[r.id] ?? r.remark_1 ?? "").trim();
    try {
      await api.put(`/receipts/${r.id}`, { ...r, application_id: appId, remark_1: remark, status: "In Progress" });
      toast.success(`Work started on ${formatBill(r.doc_no)}`);
      setAppIdEdit((s) => { const n = { ...s }; delete n[r.id]; return n; });
      setRemarkEdit((s) => { const n = { ...s }; delete n[r.id]; return n; });
      await load();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };

  // Save remark (and optional app id) WITHOUT starting work — used when work is on hold and
  // the operator needs to note down the reason (missing document, customer callback, etc.)
  const savePending = async (r) => {
    const remark = (remarkEdit[r.id] ?? r.remark_1 ?? "").trim();
    if (!remark) {
      toast.error("Please enter a remark explaining why the work is pending");
      return;
    }
    const appId = (appIdEdit[r.id] ?? r.application_id ?? "").trim();
    try {
      await api.put(`/receipts/${r.id}`, { ...r, application_id: appId, remark_1: remark });
      toast.success(`Remark saved for ${formatBill(r.doc_no)}`);
      setRemarkEdit((s) => { const n = { ...s }; delete n[r.id]; return n; });
      await load();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };

  const markCompleted = async (r) => {
    try {
      const payload = { status: "Completed" };
      if (!r.application_id) {
        const app = (window.prompt(`Application ID is required to mark ${formatBill(r.doc_no)} as Completed.\n\nEnter Application ID:`) || "").trim();
        if (!app) return toast.error("Application ID is required");
        payload.application_id = app;
      }
      await api.patch(`/receipts/${r.id}/status`, payload);
      toast.success(`Marked ${formatBill(r.doc_no)} as Completed`);
      await load();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };

  const saveCollector = async (r) => {
    const name = (collectorEdit[r.id] || "").trim();
    if (!name) return toast.error("Enter the collector's name");
    try {
      await api.put(`/receipts/${r.id}`, { ...r, collector_name: name });
      toast.success(`Collector saved for ${formatBill(r.doc_no)} — now locked`);
      setCollectorEdit((s) => { const n = { ...s }; delete n[r.id]; return n; });
      await load();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };

  const overrideStatus = async (r, newStatus) => {
    if (newStatus === r.status) return;
    // App-ID gate: moving forward from Pending requires an application id on the receipt
    let extraAppId = null;
    if (["In Progress", "Completed", "Delivered"].includes(newStatus) && !r.application_id) {
      const app = (window.prompt(`Application ID is required before marking ${formatBill(r.doc_no)} as ${newStatus}.\n\nEnter Application ID:`) || "").trim();
      if (!app) return toast.error("Application ID is required to move status forward");
      extraAppId = app;
    }
    // When moving to Delivered and no collector is saved yet, prompt for it inline
    if (newStatus === "Delivered" && !r.collector_name) {
      const name = (window.prompt(`Marking ${formatBill(r.doc_no)} as Delivered.\n\nEnter the name of the person collecting the service:`) || "").trim();
      if (!name) return toast.error("Collector name is required before Delivered");
      try {
        const payload = { status: "Delivered", collector_name: name };
        if (extraAppId) payload.application_id = extraAppId;
        await api.patch(`/receipts/${r.id}/status`, payload);
        toast.success(`Delivered — collector saved as ${name}`);
        await load();
      } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
      return;
    }
    try {
      const payload = { status: newStatus };
      if (extraAppId) payload.application_id = extraAppId;
      await api.patch(`/receipts/${r.id}/status`, payload);
      toast.success(`Status changed to ${newStatus}`);
      await load();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };
  const overrideOperator = async (r, newOperatorName) => {
    if (newOperatorName === r.operator) return;
    try {
      await api.put(`/receipts/${r.id}`, { ...r, operator: newOperatorName });
      toast.success(`Reassigned to ${newOperatorName}`);
      await load();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };

  const toggleUrgent = async (r) => {
    if (r.is_urgent) {
      // Clearing — no reason needed
      try {
        await api.patch(`/receipts/${r.id}/urgent`, { is_urgent: false });
        toast.success(`Removed urgent flag on ${formatBill(r.doc_no)}`);
        await load();
      } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
      return;
    }
    // Marking urgent — open reason dialog
    setUrgentDlg({ open: true, row: r });
  };

  const confirmUrgent = async (r, reason) => {
    try {
      await api.patch(`/receipts/${r.id}/urgent`, { is_urgent: true, urgent_reason: reason });
      toast.success(`Marked ${formatBill(r.doc_no)} as URGENT`);
      await load();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
  };

  // Admin-only Excel export honouring the current date range + status scope of this page.
  const exportExcel = async () => {
    try {
      const params = new URLSearchParams({
        rng: "Custom",
        date_from: dateFrom,
        date_to: dateTo,
        type: "Service",
      });
      if (statusFilter) params.set("status", statusFilter);
      const res = await api.get(`/export/receipts?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${testid}-${dateFrom}_to_${dateTo}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "Export failed"); }
  };

  return (
    <div className="space-y-4" data-testid={testid}>
      <div className="bg-white border border-slate-200 rounded-md p-4 flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-[10px] font-semibold uppercase tracking-wider">Range</Label>
          <Select value={rng} onValueChange={setRng}>
            <SelectTrigger className="h-9 w-36" data-testid="assigned-range"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {RANGES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] font-semibold uppercase tracking-wider">From</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-40" />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] font-semibold uppercase tracking-wider">To</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-40" />
        </div>
        {isPrivileged && (
          <div className="space-y-1">
            <Label className="text-[10px] font-semibold uppercase tracking-wider">Operator</Label>
            <Select value={operator} onValueChange={setOperator}>
              <SelectTrigger className="h-9 w-56" data-testid="assigned-operator"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                <SelectItem value="all">— All Operators —</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.name}>{u.name} — {u.role}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <Button onClick={load} disabled={loading} className="h-9 bg-amber-500 hover:bg-amber-600 text-white font-semibold" data-testid="assigned-refresh">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        {isAdmin && (
          <>
            <Button
              onClick={exportExcel}
              className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
              data-testid={`${testid}-export-btn`}
              title="Admin: Download this list as Excel (current date range)"
            >
              <FileDown className="h-4 w-4 mr-1.5" /> Export Excel
            </Button>
            <Button
              onClick={async () => {
                try {
                  const res = await api.get(`/export/receipts?all=true${statusFilter ? `&status=${encodeURIComponent(statusFilter)}&type=Service` : ""}`, { responseType: "blob" });
                  const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url; a.download = `${testid}-ALL.xlsx`;
                  document.body.appendChild(a); a.click(); a.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success("Downloaded ALL");
                } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "Export failed"); }
              }}
              className="h-9 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
              data-testid={`${testid}-export-all-btn`}
              title="Admin: download every historical record in one click (ignores date range)"
            >
              <FileDown className="h-4 w-4 mr-1.5" /> Export ALL
            </Button>
            <ImportButton resource="receipts" onDone={load} label="Import Excel" />
          </>
        )}
        <div className="space-y-1 flex-1 min-w-[180px] max-w-xs">
          <Label className="text-[10px] font-semibold uppercase tracking-wider">Search (name / mobile / App ID)</Label>
          <div className="relative">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Type name, mobile, App ID or Doc No…"
              className="h-9 pl-8 pr-8"
              data-testid="assigned-search"
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
        <div className="flex-1" />
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
            Auto-refresh 30s
            {lastSync && <span className="ml-1 text-slate-400 normal-case">· {lastSync.toLocaleTimeString([], {hour: "2-digit", minute: "2-digit", second: "2-digit"})}</span>}
          </div>
          <div className="font-display font-black text-2xl font-mono-num" data-testid="assigned-records-count">{rows.length}</div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-4 py-2.5 border-b-2 border-[color:var(--brand-teal)] bg-slate-50">
          <div className="font-display font-bold text-slate-900 tracking-tight">{title}</div>
          {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm crm-table">
            <thead className="bg-slate-100 text-slate-700 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left font-semibold w-10">Sr.</th>
                <th className="px-2 py-2 text-center font-semibold w-12" title="Urgent">
                  <AlarmClock className="h-3.5 w-3.5 inline text-red-600" />
                </th>
                <th className="px-3 py-2 text-left font-semibold">Doc No</th>
                <th className="px-3 py-2 text-left font-semibold">Date</th>
                <th className="px-3 py-2 text-left font-semibold">Customer</th>
                <th className="px-3 py-2 text-left font-semibold">Service</th>
                <th className="px-3 py-2 text-left font-semibold">Application ID</th>
                <th className="px-3 py-2 text-left font-semibold">Remark (Pending Reason)</th>
                <th className="px-3 py-2 text-left font-semibold">Operator</th>
                <th className="px-3 py-2 text-left font-semibold">Status</th>
                <th className="px-3 py-2 text-left font-semibold" title="Date when work was started (moved to In Progress)">In Progress</th>
                <th className="px-3 py-2 text-left font-semibold" title="Date when service was marked Completed">Completed</th>
                <th className="px-3 py-2 text-left font-semibold" title="Date when receipt was Delivered">Delivered</th>
                <th className="px-3 py-2 text-left font-semibold" title="Person who collected the service on delivery — locked once saved">Collector</th>
                <th className="px-3 py-2 text-right font-semibold w-52">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pageRows.map((r, i) => (
                <tr key={r.id} data-testid="assigned-row" className={r.is_urgent ? "bg-red-50 border-l-4 border-l-red-500" : ""}>
                  <td className="px-3 py-2 text-slate-500 font-mono-num">{(page - 1) * 50 + i + 1}</td>
                  <td className="px-2 py-2 text-center">
                    {canFlagUrgent ? (
                      <button
                        onClick={() => toggleUrgent(r)}
                        data-testid={`assigned-toggle-urgent-${r.id}`}
                        title={r.is_urgent ? `Urgent: ${r.urgent_reason || "no reason"}` : "Mark as URGENT"}
                        className={`inline-flex items-center justify-center h-7 w-7 rounded-full transition-colors ${
                          r.is_urgent ? "bg-red-600 text-white shadow-sm animate-pulse" : "bg-slate-100 text-slate-400 hover:bg-red-100 hover:text-red-600"
                        }`}
                      >
                        <AlarmClock className="h-3.5 w-3.5" />
                      </button>
                    ) : (
                      r.is_urgent ? (
                        <span title={r.urgent_reason || ""} className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-red-600 text-white">
                          <AlarmClock className="h-3.5 w-3.5" />
                        </span>
                      ) : <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono-num font-semibold">
                    {formatBill(r.doc_no)}
                    {r.is_urgent && r.urgent_reason && (
                      <div className="text-[10px] text-red-600 font-normal italic mt-0.5 line-clamp-2 max-w-[220px]" title={r.urgent_reason}>
                        Urgent: {r.urgent_reason}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono-num text-slate-600">{r.doc_date}</td>
                  <td className="px-3 py-2">
                    {r.customer_name}
                    <div className="text-[10px] text-slate-400 font-mono-num">{r.mobile}</div>
                  </td>
                  <td className="px-3 py-2 text-slate-700">{r.service_name}</td>
                  <td className="px-3 py-2">
                    {showActionButton && r.status === "Pending" ? (
                      <Input
                        value={appIdEdit[r.id] ?? r.application_id ?? ""}
                        onChange={(e) => setAppIdEdit((s) => ({ ...s, [r.id]: e.target.value }))}
                        placeholder="Enter App ID"
                        className="h-8 text-xs font-mono-num w-36"
                        data-testid={`app-id-input-${r.id}`}
                      />
                    ) : (
                      <span className="font-mono-num text-slate-700">{r.application_id || "—"}</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {showActionButton && r.status === "Pending" ? (
                      <Input
                        value={remarkEdit[r.id] ?? r.remark_1 ?? ""}
                        onChange={(e) => setRemarkEdit((s) => ({ ...s, [r.id]: e.target.value }))}
                        placeholder="Reason if pending…"
                        className="h-8 text-xs w-48"
                        data-testid={`remark-input-${r.id}`}
                      />
                    ) : (
                      <span className="text-xs text-slate-500 italic">{r.remark_1 || "—"}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    {canChangeOperator ? (
                      <Select value={r.operator || ""} onValueChange={(v) => overrideOperator(r, v)}>
                        <SelectTrigger className="h-7 text-xs w-40" data-testid={`override-operator-${r.id}`} title="Reassign operator"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {users.map((u) => <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      r.operator || "—"
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {(() => {
                      const isLockedForMe = r.status_locked_by_operator && !canOverrideStatus;
                      const statusBadge = (
                        <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${
                          r.status === "Delivered" ? "bg-purple-50 text-purple-700 border-purple-200" :
                          r.status === "Completed" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                          r.status === "In Progress" ? "bg-blue-50 text-blue-700 border-blue-200" :
                          "bg-amber-50 text-amber-700 border-amber-200"
                        }`}>{r.status || "Pending"}</span>
                      );
                      if (canChangeStatus && !isLockedForMe) {
                        return (
                          <Select value={r.status || "Pending"} onValueChange={(v) => overrideStatus(r, v)}>
                            <SelectTrigger className={`h-7 text-[10px] w-32 font-semibold uppercase tracking-wider ${
                              r.status === "Delivered" ? "bg-purple-50 text-purple-700 border-purple-200" :
                              r.status === "Completed" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                              r.status === "In Progress" ? "bg-blue-50 text-blue-700 border-blue-200" :
                              "bg-amber-50 text-amber-700 border-amber-200"
                            }`} data-testid={`override-status-${r.id}`} title="Change status"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-white">
                              {["Pending", "In Progress", "Completed", "Delivered"].map((s) => (
                                // All four visible; backend enforces forward-only for non-admin/manager.
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        );
                      }
                      return (
                        <div data-testid={`status-locked-${r.id}`}>
                          {statusBadge}
                          {r.status_locked_by_operator && (
                            <div
                              className="text-[9px] text-red-600 font-semibold uppercase mt-0.5 inline-flex items-center gap-0.5"
                              title={`Status changed by ${r.status_changed_by || "operator"}${r.status_changed_at ? " at " + r.status_changed_at : ""} — admin/manager only`}
                            >
                              <ShieldAlert className="h-2.5 w-2.5" /> Locked
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-2 font-mono-num text-xs text-slate-600" data-testid={`in-progress-date-${r.id}`}>{r.in_progress_date || "—"}</td>
                  <td className="px-3 py-2 font-mono-num text-xs text-slate-600" data-testid={`completed-date-${r.id}`}>{r.completed_date || "—"}</td>
                  <td className="px-3 py-2 font-mono-num text-xs text-slate-600" data-testid={`delivered-date-${r.id}`}>{r.delivered_date || "—"}</td>
                  <td className="px-3 py-2">
                    {r.collector_name ? (
                      <div className="flex flex-col" data-testid={`collector-locked-${r.id}`}>
                        <span className="font-semibold text-slate-800 text-xs inline-flex items-center gap-1">
                          <ShieldAlert className="h-3 w-3 text-emerald-600" />
                          {r.collector_name}
                        </span>
                        <span className="text-[10px] text-slate-400" title={`Saved by ${r.collector_saved_by || "?"} at ${r.collector_saved_at || ""}`}>Locked</span>
                      </div>
                    ) : canChangeStatus ? (
                      <div className="flex items-center gap-1">
                        <Input
                          value={collectorEdit[r.id] ?? ""}
                          onChange={(e) => setCollectorEdit((s) => ({ ...s, [r.id]: e.target.value }))}
                          placeholder="Collector name"
                          className="h-7 text-xs w-32"
                          data-testid={`collector-input-${r.id}`}
                        />
                        <button
                          onClick={() => saveCollector(r)}
                          disabled={!(collectorEdit[r.id] || "").trim()}
                          className="px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-[11px] font-semibold"
                          data-testid={`collector-save-${r.id}`}
                          title="Save (locked after save)"
                        >Save</button>
                      </div>
                    ) : (
                      <span className="text-slate-400 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1.5">
                      {showActionButton && r.status === "Pending" && (
                        <>
                          <button
                            onClick={() => savePending(r)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-600 hover:bg-slate-700 text-white text-xs font-semibold"
                            data-testid={`save-remark-btn-${r.id}`}
                            title="Save remark without starting work"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => takeAction(r)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold"
                            data-testid={`take-action-btn-${r.id}`}
                          >
                            <Play className="h-3 w-3" /> Start Work
                          </button>
                        </>
                      )}
                      {r.status === "In Progress" && (
                        <button
                          onClick={() => markCompleted(r)}
                          disabled={r.status_locked_by_operator && !canOverrideStatus}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-semibold"
                          data-testid={`mark-completed-btn-${r.id}`}
                          title={r.status_locked_by_operator && !canOverrideStatus ? `Locked by ${r.status_changed_by || "operator"} — admin/manager only` : "Mark this work as completed"}
                        >
                          <PackageCheck className="h-3 w-3" /> Mark Completed
                        </button>
                      )}
                      {r.status === "Completed" && (
                        <button
                          onClick={() => {
                            if (!r.mobile) { toast.error("No mobile number on this record"); return; }
                            setWaRow(r);
                          }}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold"
                          data-testid={`assigned-wa-btn-${r.id}`}
                          title="Send collection-ready WhatsApp message"
                        >
                          <MessageSquare className="h-3 w-3" /> WhatsApp
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && !loading && (
            <div className="py-10 text-center text-slate-400 text-sm">{emptyLabel}</div>
          )}
        </div>
        <Pagination page={page} setPage={setPage} totalPages={totalPages} total={total} pageSize={pageSize} setPageSize={setPageSize} testid={`${testid}-pagination`} />
      </div>
      <UrgentReasonDialog
        open={urgentDlg.open}
        onOpenChange={(v) => setUrgentDlg((s) => ({ ...s, open: v }))}
        row={urgentDlg.row}
        onConfirm={confirmUrgent}
      />
      {waRow && (
        <WhatsAppMessageModal
          open={!!waRow}
          onOpenChange={(v) => { if (!v) setWaRow(null); }}
          mobile={waRow.mobile}
          customerName={waRow.customer_name}
          subtitle={`Doc ${formatBill(waRow.doc_no)} · ${waRow.service_name || ""}`}
          defaultMessage={buildCollectionReadyMessage(waRow.customer_name)}
          title="Ready for Collection — Notify Customer"
        />
      )}
    </div>
  );
}

export function DailyAssignedService() {
  return (
    <AssignedListPage
      endpoint="/daily-assigned-service"
      testid="daily-assigned-page"
      title="Daily Assigned Service"
      subtitle="Pending services awaiting action. Enter the Application ID and start work — the row automatically transfers to Daily Login Service."
      emptyLabel="No pending assigned services in the selected range"
      statusFilter="Pending"
      showActionButton
    />
  );
}

export function DailyLoginService() {
  return (
    <AssignedListPage
      endpoint="/daily-login-service"
      testid="daily-login-page"
      title="Daily Login Service"
      subtitle="Assigned work that has been actioned — In Progress, Completed and Delivered receipts with Application IDs."
      emptyLabel="No actioned work in the selected range"
      statusFilter="In Progress,Completed,Delivered"
    />
  );
}
